// ============================================
// Captain Pane — Live Clinical Analysis Output
//
// Dual-pane test view (Captain only, Nurse TBD).
// Mobile-first. Uses AnalystBar header (2-line layout)
// from PatientChatEngine as template.
//
// Integrates comparisonEngine + docService for
// real-time clinical analysis display.
// ============================================

import { useState, useEffect, useRef, useCallback } from 'react';
import { AnalystBar } from './analyst-bar/AnalystBar';
import type { BarMarker, BarSymptom, BarProtocol } from '../../types/analystBar';
import { generateCase, runAIAnalysis, computeDivergences, computeAlignmentScore } from '../../services/patientJourney/docService';
import type { DocCase, DoctorProtocolItem, Divergence } from '../../services/patientJourney/docService';
// generateComparisonSummary — available for future use when Nurse pane is added
// import { generateComparisonSummary } from '../../services/patientJourney/comparisonEngine';
import type { BiomarkerResult, FullReport } from '../../types';

// -- Design Tokens (match PatientChatEngine) --
const ROSE = '#E11D48';
const ROSE_LIGHT = 'rgba(225,29,72,0.08)';
const BG = '#FAFAFA';
const CARD = '#FFFFFF';
const BORDER = '#E4E4E7';
const TEXT = '#18181B';
const TEXT_MUTED = '#71717A';
const TEXT_FAINT = '#A1A1AA';
const GREEN = '#16A34A';
const GREEN_LIGHT = 'rgba(22,163,74,0.08)';
const RED = '#EF4444';
const AMBER = '#D97706';
const R = 4;
// const R_LG = 12;
const SHADOW_SM = '0 1px 3px rgba(0,0,0,0.04)';
const W = '#FFFFFF';

const FONT_BODY = "'DM Sans',sans-serif";
const FONT_SERIF = "'Instrument Serif',serif";
const FONT_MONO = "'JetBrains Mono',monospace";

// -- Icons --
const HeartPulseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={ROSE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
    <path d="M3.22 12H9.5l.5-1 2 4.5 2-7 1.5 3.5h5.27" />
  </svg>
);
const ShieldIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={TEXT_FAINT} strokeWidth="2" strokeLinecap="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

// -- Status mapping for biomarkers -> BarMarker flags --
function statusToFlag(status: BiomarkerResult['hemologixStatus']): BarMarker['flag'] {
  switch (status) {
    case 'critical':
    case 'low':
    case 'elevated':
      return 'attention';
    case 'suboptimal':
      return 'borderline';
    case 'optimal':
      return 'optimal';
    default:
      return 'pending';
  }
}

function biomarkerToBarMarker(bm: BiomarkerResult): BarMarker {
  return {
    id: bm.biomarker,
    name: bm.biomarker,
    value: bm.value,
    flag: statusToFlag(bm.hemologixStatus),
  };
}

// -- Alignment badge colors --
function alignmentColor(alignment: 'aligned' | 'delta' | 'both_missed'): string {
  if (alignment === 'aligned') return GREEN;
  if (alignment === 'delta') return RED;
  return AMBER;
}

function alignmentBg(alignment: 'aligned' | 'delta' | 'both_missed'): string {
  if (alignment === 'aligned') return GREEN_LIGHT;
  if (alignment === 'delta') return 'rgba(239,68,68,0.08)';
  return 'rgba(217,119,6,0.08)';
}

function alignmentLabel(alignment: 'aligned' | 'delta' | 'both_missed'): string {
  if (alignment === 'aligned') return 'Aligned';
  if (alignment === 'delta') return 'Delta';
  return 'Both Missed';
}

// -- Analysis log entry --
interface LogEntry {
  id: number;
  type: 'status' | 'marker' | 'divergence' | 'summary' | 'error';
  text: string;
  timestamp: Date;
  detail?: string;
  alignment?: 'aligned' | 'delta' | 'both_missed';
}

// ── Main Component ──
export function CaptainPane() {
  const [log, setLog] = useState<LogEntry[]>([]);
  const [tickerMarkers, setTickerMarkers] = useState<BarMarker[]>([]);
  const [symptoms, setSymptoms] = useState<BarSymptom[]>([]);
  const [captainMsg, setCaptainMsg] = useState<string | null>(null);
  const [protocol, setProtocol] = useState<BarProtocol | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [, setCaseData] = useState<DocCase | null>(null);
  const [alignmentScore, setAlignmentScore] = useState<number | null>(null);
  const [divergences, setDivergences] = useState<Divergence[]>([]);
  const [aiReport, setAiReport] = useState<FullReport | null>(null);
  const [tier, setTier] = useState(3);
  const scrollRef = useRef<HTMLDivElement>(null);
  const logIdRef = useRef(1);

  // Auto-scroll log
  useEffect(() => {
    if (scrollRef.current) {
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 80);
    }
  }, [log, captainMsg, aiReport]);

  const addLog = useCallback((entry: Omit<LogEntry, 'id' | 'timestamp'>) => {
    setLog(prev => [...prev, { ...entry, id: logIdRef.current++, timestamp: new Date() }]);
  }, []);

  const wait = (ms: number) => new Promise(r => setTimeout(r, ms));

  // -- Run analysis pipeline --
  const runAnalysis = useCallback(async () => {
    if (isRunning) return;
    setIsRunning(true);
    setLog([]);
    setTickerMarkers([]);
    setSymptoms([]);
    setProtocol(null);
    setCaptainMsg(null);
    setCaseData(null);
    setAlignmentScore(null);
    setDivergences([]);
    setAiReport(null);

    try {
      // Phase 1: Generate case
      setCaptainMsg('Generating synthetic patient case');
      addLog({ type: 'status', text: 'Generating patient case...' });
      await wait(400);

      const docCase = await generateCase(tier, 'female', 38);
      setCaseData(docCase);

      addLog({
        type: 'status',
        text: `Case generated: ${docCase.biomarkers.length} biomarkers`,
        detail: `Demographics: Female, 38 \u00B7 Concerns: ${docCase.concerns.join(', ')}`,
      });
      await wait(600);

      // Phase 2: Stream biomarkers into AnalystBar
      setCaptainMsg('Analyzing biomarkers against functional ranges');
      addLog({ type: 'status', text: 'Analyzing biomarkers...' });
      await wait(400);

      const flagged: BiomarkerResult[] = [];
      for (let i = 0; i < docCase.biomarkers.length; i++) {
        const bm = docCase.biomarkers[i];
        const marker = biomarkerToBarMarker(bm);

        // Show pending first, then resolve
        setTickerMarkers(prev => [...prev, { ...marker, flag: 'pending' }]);
        await wait(150);
        setTickerMarkers(prev => prev.map((m, mi) => mi === i ? marker : m));
        await wait(200);

        if (bm.hemologixStatus !== 'optimal') {
          flagged.push(bm);
          addLog({
            type: 'marker',
            text: `${bm.biomarker}: ${bm.value} ${bm.unit}`,
            detail: bm.interpretation,
            alignment: bm.hemologixStatus === 'critical' ? 'delta' : 'both_missed',
          });
        }
      }

      // Add concern-based symptoms
      if (docCase.concerns.length > 0) {
        const barSymptoms: BarSymptom[] = docCase.concerns.slice(0, 3).map(c => ({
          term: c.split(',')[0].trim().toUpperCase(),
        }));
        setSymptoms(barSymptoms);
      }
      await wait(500);

      setCaptainMsg(`${flagged.length} markers flagged \u00B7 running AI protocol`);
      addLog({
        type: 'status',
        text: `${flagged.length} of ${docCase.biomarkers.length} markers suboptimal or flagged`,
      });
      await wait(800);

      // Phase 3: Run AI analysis
      setCaptainMsg('Running AI protocol builder + report generator');
      addLog({ type: 'status', text: 'Running AI analysis (protocol + report)...' });

      const { aiProtocol, aiReport: report } = await runAIAnalysis(
        docCase.biomarkers,
        docCase.demographics,
      );
      setAiReport(report);

      addLog({
        type: 'status',
        text: `AI protocol: ${aiProtocol.supplements.length} supplements recommended`,
        detail: aiProtocol.supplements.map(s => s.name).join(', '),
      });
      await wait(400);

      // Show protocol in AnalystBar
      if (aiProtocol.supplements.length > 0) {
        const barProducts = aiProtocol.supplements.map(s => ({
          name: s.name,
          monthlyPrice: 39, // Default display price
        }));
        const dailyPrice = (barProducts.length * 39) / 30;
        setProtocol({
          gateLabel: 'GATE 1 \u00B7 AI PROTOCOL',
          products: barProducts.slice(0, 3),
          dailyPrice,
        });
      }
      await wait(600);

      // Phase 4: Compute divergences (mock doctor protocol for demo)
      setCaptainMsg('Computing divergences \u00B7 Doctor vs AI');
      addLog({ type: 'status', text: 'Computing protocol divergences...' });

      const mockDoctorProtocol: DoctorProtocolItem[] = aiProtocol.supplements.slice(0, 2).map(s => ({
        productId: s.name.toLowerCase().replace(/\s+/g, '-'),
        name: s.name,
        dosage: s.dosage,
        timing: s.timing,
      }));

      const divs = computeDivergences(mockDoctorProtocol, aiProtocol);
      setDivergences(divs);

      for (const div of divs) {
        addLog({
          type: 'divergence',
          text: `${div.productName}: ${div.type}`,
          detail: div.reasoning,
          alignment: div.type === 'doctor_only' || div.type === 'ai_only' ? 'delta' : 'both_missed',
        });
        await wait(300);
      }

      // Phase 5: Alignment score
      const score = computeAlignmentScore(mockDoctorProtocol, aiProtocol);
      setAlignmentScore(score);

      setCaptainMsg(`Alignment: ${score}% \u00B7 ${divs.length} divergence${divs.length !== 1 ? 's' : ''}`);
      addLog({
        type: 'summary',
        text: `Alignment score: ${score}%`,
        detail: `${divs.filter(d => d.type === 'ai_only').length} AI-only, ${divs.filter(d => d.type === 'doctor_only').length} Doctor-only, ${divs.filter(d => d.type === 'dosage' || d.type === 'timing').length} dosage/timing deltas`,
      });

      await wait(2000);
      setCaptainMsg(null);

    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setCaptainMsg(null);
      addLog({ type: 'error', text: `Analysis failed: ${msg}` });
    } finally {
      setIsRunning(false);
    }
  }, [isRunning, tier, addLog]);

  return (
    <div style={{
      width: '100%',
      maxWidth: 480,
      height: '100vh',
      margin: '0 auto',
      display: 'flex',
      flexDirection: 'column',
      background: BG,
      fontFamily: FONT_BODY,
      position: 'relative',
      overflow: 'hidden',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&family=Instrument+Serif&family=JetBrains+Mono:wght@400;500;700&display=swap');
        *{box-sizing:border-box;margin:0;padding:0} body{margin:0;background:${BG}}
        ::-webkit-scrollbar{width:0;height:0}
        @keyframes msgIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}
      `}</style>

      {/* ── Header (2-line mobile layout from PatientChatEngine) ── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        background: CARD,
        borderBottom: `1px solid ${BORDER}`,
        boxShadow: SHADOW_SM,
        zIndex: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            background: ROSE_LIGHT,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <HeartPulseIcon />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: TEXT }}>
              Captain <span style={{ color: ROSE }}>Pane</span>
            </div>
            <div style={{
              fontSize: 11, color: TEXT_FAINT,
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              <div style={{
                width: 6, height: 6, borderRadius: '50%',
                background: isRunning ? ROSE : GREEN,
                animation: isRunning ? 'pulse 1.5s ease infinite' : 'none',
              }} />
              {isRunning ? 'Analyzing...' : 'Clinical Analysis'}
            </div>
          </div>
        </div>
        <div style={{
          fontSize: 11, fontWeight: 600, color: TEXT_FAINT,
          display: 'flex', alignItems: 'center', gap: 4,
        }}>
          <ShieldIcon /> LIVE TEST
        </div>
      </div>

      {/* ── AnalystBar ── */}
      <AnalystBar
        markers={tickerMarkers}
        symptoms={symptoms}
        captainMsg={captainMsg}
        protocol={protocol}
        journeyStage="first_visit"
      />

      {/* ── Alignment Score Banner ── */}
      {alignmentScore !== null && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 16px',
          background: alignmentScore >= 70 ? GREEN_LIGHT : 'rgba(239,68,68,0.08)',
          borderBottom: `1px solid ${BORDER}`,
          animation: 'fadeIn 0.4s ease',
        }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: TEXT, fontFamily: FONT_BODY }}>
            Alignment Score
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              fontSize: 20, fontWeight: 700,
              color: alignmentScore >= 70 ? GREEN : RED,
              fontFamily: FONT_MONO,
            }}>
              {alignmentScore}%
            </span>
            <span style={{
              fontSize: 11, color: TEXT_MUTED,
              fontFamily: FONT_BODY,
            }}>
              {divergences.length} divergence{divergences.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      )}

      {/* ── Log Stream ── */}
      <div ref={scrollRef} style={{
        flex: 1,
        overflowY: 'auto',
        padding: '12px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}>
        {log.length === 0 && !isRunning && (
          <div style={{
            flex: 1, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            textAlign: 'center', padding: '40px 20px',
          }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: ROSE_LIGHT,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 16,
            }}>
              <HeartPulseIcon />
            </div>
            <div style={{
              fontFamily: FONT_SERIF, fontSize: 22, color: TEXT,
              marginBottom: 6,
            }}>
              Captain Analysis View
            </div>
            <div style={{ fontSize: 14, color: TEXT_MUTED, lineHeight: 1.5, maxWidth: 280 }}>
              Real-time clinical analysis output. Generate a case and watch the comparison engine work.
            </div>
          </div>
        )}

        {log.map(entry => (
          <div
            key={entry.id}
            style={{
              padding: '10px 12px',
              background: CARD,
              border: `1px solid ${BORDER}`,
              borderLeft: entry.type === 'error'
                ? `3px solid ${RED}`
                : entry.type === 'divergence'
                  ? `3px solid ${alignmentColor(entry.alignment || 'delta')}`
                  : entry.type === 'summary'
                    ? `3px solid ${GREEN}`
                    : `3px solid ${BORDER}`,
              borderRadius: R,
              animation: 'msgIn 0.3s ease',
              boxShadow: SHADOW_SM,
            }}
          >
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: entry.detail ? 4 : 0,
            }}>
              <div style={{
                fontSize: 13, fontWeight: 600, color: TEXT,
                fontFamily: FONT_BODY,
                flex: 1, minWidth: 0,
              }}>
                {entry.text}
              </div>
              {entry.alignment && (
                <span style={{
                  padding: '2px 8px',
                  borderRadius: 20,
                  background: alignmentBg(entry.alignment),
                  color: alignmentColor(entry.alignment),
                  fontSize: 11, fontWeight: 600,
                  fontFamily: FONT_BODY,
                  flexShrink: 0,
                  marginLeft: 8,
                }}>
                  {alignmentLabel(entry.alignment)}
                </span>
              )}
              {entry.type === 'status' && (
                <span style={{
                  fontSize: 10, color: TEXT_FAINT, fontFamily: FONT_MONO, flexShrink: 0, marginLeft: 8,
                }}>
                  {entry.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </span>
              )}
            </div>
            {entry.detail && (
              <div style={{
                fontSize: 13, color: TEXT_MUTED,
                fontFamily: FONT_BODY, lineHeight: 1.5,
                marginTop: 2,
              }}>
                {entry.detail}
              </div>
            )}
          </div>
        ))}

        {/* ── Captain's Analysis Report ── */}
        {aiReport && !isRunning && (
          <div style={{
            marginTop: 12,
            animation: 'fadeIn 0.5s ease',
          }}>
            {/* Report Header */}
            <div style={{
              padding: '14px 14px 10px',
              background: CARD,
              border: `1px solid ${BORDER}`,
              borderLeft: `3px solid ${ROSE}`,
              borderRadius: R,
              boxShadow: SHADOW_SM,
              marginBottom: 6,
            }}>
              <div style={{
                fontFamily: FONT_SERIF,
                fontSize: 20,
                color: TEXT,
                marginBottom: 4,
              }}>
                Captain's Analysis Report
              </div>
              <div style={{
                fontSize: 13,
                color: TEXT_MUTED,
                fontFamily: FONT_MONO,
              }}>
                {aiReport.biomarkerCount} markers analyzed · {aiReport.flaggedCount} flagged
              </div>
            </div>

            {/* Executive Summary */}
            <div style={{
              padding: '12px 14px',
              background: CARD,
              border: `1px solid ${BORDER}`,
              borderRadius: R,
              boxShadow: SHADOW_SM,
              marginBottom: 6,
            }}>
              <div style={{
                fontSize: 13,
                fontWeight: 700,
                color: ROSE,
                fontFamily: FONT_BODY,
                marginBottom: 6,
                textTransform: 'uppercase' as const,
                letterSpacing: 0.5,
              }}>
                Executive Summary
              </div>
              <div style={{
                fontSize: 14,
                color: TEXT,
                fontFamily: FONT_BODY,
                lineHeight: 1.6,
              }}>
                {aiReport.executiveSummary}
              </div>
            </div>

            {/* Key Findings */}
            {aiReport.keyFindings.length > 0 && (
              <div style={{
                padding: '12px 14px',
                background: CARD,
                border: `1px solid ${BORDER}`,
                borderRadius: R,
                boxShadow: SHADOW_SM,
                marginBottom: 6,
              }}>
                <div style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: ROSE,
                  fontFamily: FONT_BODY,
                  marginBottom: 8,
                  textTransform: 'uppercase' as const,
                  letterSpacing: 0.5,
                }}>
                  Key Findings
                </div>
                {aiReport.keyFindings.map((finding, i) => (
                  <div key={i} style={{
                    padding: '8px 10px',
                    background: finding.severity === 'urgent'
                      ? 'rgba(239,68,68,0.06)'
                      : finding.severity === 'attention'
                        ? 'rgba(217,119,6,0.06)'
                        : 'rgba(22,163,74,0.04)',
                    borderLeft: `3px solid ${
                      finding.severity === 'urgent' ? RED
                        : finding.severity === 'attention' ? AMBER
                          : GREEN
                    }`,
                    borderRadius: R,
                    marginBottom: i < aiReport.keyFindings.length - 1 ? 6 : 0,
                  }}>
                    <div style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: TEXT,
                      fontFamily: FONT_BODY,
                      marginBottom: 3,
                    }}>
                      {finding.title}
                    </div>
                    <div style={{
                      fontSize: 13,
                      color: TEXT_MUTED,
                      fontFamily: FONT_BODY,
                      lineHeight: 1.5,
                    }}>
                      {finding.content}
                    </div>
                    {finding.biomarkers.length > 0 && (
                      <div style={{
                        display: 'flex',
                        flexWrap: 'wrap' as const,
                        gap: 4,
                        marginTop: 6,
                      }}>
                        {finding.biomarkers.map((bm, bi) => (
                          <span key={bi} style={{
                            fontSize: 11,
                            fontFamily: FONT_MONO,
                            padding: '2px 6px',
                            background: ROSE_LIGHT,
                            color: ROSE,
                            borderRadius: 20,
                            fontWeight: 500,
                          }}>
                            {bm}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Category Analyses */}
            {aiReport.categoryAnalyses.length > 0 && (
              <div style={{
                padding: '12px 14px',
                background: CARD,
                border: `1px solid ${BORDER}`,
                borderRadius: R,
                boxShadow: SHADOW_SM,
                marginBottom: 6,
              }}>
                <div style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: ROSE,
                  fontFamily: FONT_BODY,
                  marginBottom: 8,
                  textTransform: 'uppercase' as const,
                  letterSpacing: 0.5,
                }}>
                  Category Analysis
                </div>
                {aiReport.categoryAnalyses.map((cat, i) => (
                  <div key={i} style={{
                    padding: '8px 10px',
                    borderBottom: i < aiReport.categoryAnalyses.length - 1
                      ? `1px solid ${BORDER}` : 'none',
                  }}>
                    <div style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: TEXT,
                      fontFamily: FONT_BODY,
                      marginBottom: 3,
                    }}>
                      {cat.title}
                    </div>
                    <div style={{
                      fontSize: 13,
                      color: TEXT_MUTED,
                      fontFamily: FONT_BODY,
                      lineHeight: 1.5,
                    }}>
                      {cat.content}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pattern Connections */}
            {aiReport.patternConnections.length > 0 && (
              <div style={{
                padding: '12px 14px',
                background: CARD,
                border: `1px solid ${BORDER}`,
                borderRadius: R,
                boxShadow: SHADOW_SM,
                marginBottom: 6,
              }}>
                <div style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: ROSE,
                  fontFamily: FONT_BODY,
                  marginBottom: 8,
                  textTransform: 'uppercase' as const,
                  letterSpacing: 0.5,
                }}>
                  Pattern Connections
                </div>
                {aiReport.patternConnections.map((pat, i) => (
                  <div key={i} style={{
                    padding: '8px 10px',
                    background: 'rgba(225,29,72,0.03)',
                    borderRadius: R,
                    marginBottom: i < aiReport.patternConnections.length - 1 ? 6 : 0,
                  }}>
                    <div style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: TEXT,
                      fontFamily: FONT_BODY,
                      marginBottom: 3,
                    }}>
                      {pat.title}
                    </div>
                    <div style={{
                      fontSize: 13,
                      color: TEXT_MUTED,
                      fontFamily: FONT_BODY,
                      lineHeight: 1.5,
                    }}>
                      {pat.content}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Positive Findings */}
            {aiReport.positiveFindings.length > 0 && (
              <div style={{
                padding: '12px 14px',
                background: CARD,
                border: `1px solid ${BORDER}`,
                borderLeft: `3px solid ${GREEN}`,
                borderRadius: R,
                boxShadow: SHADOW_SM,
                marginBottom: 6,
              }}>
                <div style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: GREEN,
                  fontFamily: FONT_BODY,
                  marginBottom: 8,
                  textTransform: 'uppercase' as const,
                  letterSpacing: 0.5,
                }}>
                  Positive Findings
                </div>
                {aiReport.positiveFindings.map((pf, i) => (
                  <div key={i} style={{
                    fontSize: 13,
                    color: TEXT,
                    fontFamily: FONT_BODY,
                    lineHeight: 1.5,
                    padding: '4px 0',
                    display: 'flex',
                    gap: 8,
                  }}>
                    <span style={{ color: GREEN, flexShrink: 0 }}>✓</span>
                    <span>{pf}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Next Steps */}
            {aiReport.nextSteps.length > 0 && (
              <div style={{
                padding: '12px 14px',
                background: CARD,
                border: `1px solid ${BORDER}`,
                borderLeft: `3px solid ${AMBER}`,
                borderRadius: R,
                boxShadow: SHADOW_SM,
                marginBottom: 6,
              }}>
                <div style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: AMBER,
                  fontFamily: FONT_BODY,
                  marginBottom: 8,
                  textTransform: 'uppercase' as const,
                  letterSpacing: 0.5,
                }}>
                  Recommended Next Steps
                </div>
                {aiReport.nextSteps.map((step, i) => (
                  <div key={i} style={{
                    fontSize: 13,
                    color: TEXT,
                    fontFamily: FONT_BODY,
                    lineHeight: 1.5,
                    padding: '4px 0',
                    display: 'flex',
                    gap: 8,
                  }}>
                    <span style={{
                      color: AMBER,
                      fontFamily: FONT_MONO,
                      fontSize: 11,
                      fontWeight: 700,
                      flexShrink: 0,
                      marginTop: 2,
                    }}>
                      {i + 1}.
                    </span>
                    <span>{step}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Disclaimer */}
            <div style={{
              padding: '8px 14px',
              fontSize: 11,
              color: TEXT_FAINT,
              fontFamily: FONT_BODY,
              lineHeight: 1.5,
              textAlign: 'center' as const,
            }}>
              AI-generated analysis · Pending human Dr review
            </div>
          </div>
        )}

        <div style={{ height: 8 }} />
      </div>

      {/* ── Controls Footer ── */}
      <div style={{
        padding: '10px 16px 14px',
        background: CARD,
        borderTop: `1px solid ${BORDER}`,
      }}>
        {/* Tier selector */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10,
        }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: TEXT_MUTED, fontFamily: FONT_BODY }}>
            Complexity:
          </span>
          {[1, 2, 3, 4, 5].map(t => (
            <button
              key={t}
              onClick={() => setTier(t)}
              style={{
                width: 32, height: 32,
                borderRadius: R,
                border: `1px solid ${tier === t ? ROSE : BORDER}`,
                background: tier === t ? ROSE_LIGHT : CARD,
                color: tier === t ? ROSE : TEXT_MUTED,
                fontSize: 14, fontWeight: 700,
                fontFamily: FONT_MONO,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Run button */}
        <button
          onClick={runAnalysis}
          disabled={isRunning}
          style={{
            width: '100%',
            padding: '14px 20px',
            fontSize: 15,
            fontWeight: 700,
            color: W,
            background: isRunning ? TEXT_MUTED : ROSE,
            border: 'none',
            borderRadius: R,
            cursor: isRunning ? 'not-allowed' : 'pointer',
            fontFamily: FONT_BODY,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            boxShadow: isRunning ? 'none' : '0 2px 12px rgba(225,29,72,0.3)',
          }}
        >
          {isRunning ? 'Running Analysis...' : 'Generate Case & Analyze'}
        </button>

        <div style={{
          fontSize: 10, color: TEXT_FAINT, textAlign: 'center', marginTop: 6,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
        }}>
          <ShieldIcon /> comparisonEngine + docService &middot; Live test mode
        </div>
      </div>
    </div>
  );
}
